import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ticketApi } from '../../api/ticket.api';
import { departmentApi } from '../../api/department.api';
import { useUI } from '../../store/UIContext';
import { Badge, Btn, PageHeader, EmptyState, Skeleton } from '../../components/ui/index';
import { EmojiBar } from '../../components/ui/EmojiPicker';
import { toPersian, timeAgo } from '../../utils/persianNumber';
import { validateFile, getAcceptAttribute, getLimitsHint } from '../../utils/fileValidation';
import FileDropZone from '../../components/ui/FileDropZone';
import TicketFilterBar from '../../components/tickets/TicketFilterBar';
import TicketReferralRoadmap from '../../components/tickets/TicketReferralRoadmap';
import ReferralNoteComposer from '../../components/tickets/ReferralNoteComposer';
import DashboardShortcuts from '../../components/dashboard/DashboardShortcuts';
import { useTicketFilters } from '../../hooks/useTicketFilters';
import { useAuth } from '../../store/AuthContext';
import TicketBodyPreview from '../../components/tickets/TicketBodyPreview';

// ─── Dispatcher Ticket List ─────────────────────────────────────────
export function DispatcherPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('contract');

  useEffect(() => {
    ticketApi.getTicketListView({ dispatcher_seen: 0 })
      .then((res) => setTickets(Array.isArray(res.data) ? res.data : res.data?.tickets || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const noContractCount = tickets.filter((t) => t.no_contract).length;
  const tabTickets = tickets.filter((t) => (tab === 'no_contract' ? t.no_contract : !t.no_contract));

  const { filteredSorted, company, setCompany, sortBy, setSortBy, companyOptions } =
    useTicketFilters(tabTickets, { companyKey: 'company', dateKey: 'send_date' });

  return (
    <div className="fade-in">
      <PageHeader title="صف دیسپچر" subtitle={`${toPersian(tickets.length)} تیکت برای بررسی`} />

      <DashboardShortcuts role={user?.type || 'dispatcher'} activePath="/dispatcher" />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Btn size="sm" variant={tab === 'contract' ? 'primary' : 'ghost'} onClick={() => setTab('contract')}>
          شرکت‌های دارای قرارداد ({toPersian(tickets.length - noContractCount)})
        </Btn>
        <Btn size="sm" variant={tab === 'no_contract' ? 'primary' : 'ghost'} onClick={() => setTab('no_contract')}>
          مهمان / بدون قرارداد ({toPersian(noContractCount)})
        </Btn>
      </div>

      {tickets.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <TicketFilterBar
            sortBy={sortBy} setSortBy={setSortBy}
            company={company} setCompany={setCompany}
            companyOptions={companyOptions}
          />
        </div>
      )}

      {loading
        ? <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{[1,2,3].map((k) => <SkeletonRow key={k} />)}</div>
        : filteredSorted.length === 0
          ? <EmptyState icon="✅" title="صف خالی است" subtitle="همه تیکت‌ها بررسی شده‌اند" />
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {filteredSorted.map((t) => (
                <div key={t.id || t.ticket_id} className="pj-row-card pj-card-hover" style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, cursor: 'pointer', transition: 'all 0.15s' }}
                  onClick={() => navigate(`/tickets/${t.id || t.ticket_id}/assign`)}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#bfdbfe'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                >
                  <div style={{ minWidth: 0 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 4px' }}>{t.ticket_title || t.title}</h3>
                    <TicketBodyPreview ticket={t} />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', fontSize: 12, color: '#64748b' }}>
                      {(t.sender_first_name || t.sender_last_name) && (
                        <span>👤 {[t.sender_first_name, t.sender_last_name].filter(Boolean).join(' ')}{t.sender_username ? ` (${t.sender_username})` : ''}</span>
                      )}
                      {t.company ? (
                        <span
                          onClick={(e) => { e.stopPropagation(); navigate(`/companies?search=${encodeURIComponent(t.company)}`); }}
                          title="مشاهده اطلاعات شرکت"
                          style={{ cursor: 'pointer', color: '#2563eb', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                        >
                          🏢 {t.company_display || t.company}
                        </span>
                      ) : (
                        <Badge variant="amber">مهمان</Badge>
                      )}
                      <span style={{ color: '#94a3b8' }}>🕒 {timeAgo(t.send_date)}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Badge variant="amber">نیاز به ارجاع</Badge>
                    {t.suggested_department && (
                      <Badge variant="purple" title="پیشنهاد هوش مصنوعی با اطمینان کم - نیاز به تأیید دیسپچر">
                        🤖 پیشنهاد: {t.suggested_department}
                      </Badge>
                    )}
                    <Btn size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/tickets/${t.id || t.ticket_id}/assign`); }}>انتخاب دپارتمان</Btn>
                  </div>
                </div>
              ))}
            </div>
          )
      }
    </div>
  );
}

// ─── Assigned but Unanswered Tickets ──────────────────────────────────
// Distinct from DispatcherPage's queue above: those are brand-new tickets
// still needing to be routed to a department; this is tickets already
// routed (by a human dispatcher or the AI auto-assignment) that the
// receiving department hasn't answered yet.
export function AssignedUnansweredTicketsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ticketApi.getAssignedUnansweredTickets()
      .then((res) => setTickets(Array.isArray(res.data) ? res.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="fade-in">
      <PageHeader title="تیکت‌های ارجاع‌شده" subtitle={`${toPersian(tickets.length)} تیکت در انتظار پاسخ دپارتمان`} />

      <DashboardShortcuts role={user?.type || 'dispatcher'} activePath="/dispatcher/assigned" />

      {loading
        ? <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{[1,2,3].map((k) => <SkeletonRow key={k} />)}</div>
        : tickets.length === 0
          ? <EmptyState icon="✅" title="موردی یافت نشد" subtitle="همه تیکت‌های ارجاع‌شده پاسخ داده شده‌اند" />
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {tickets.map((t) => (
                <div key={t.id} className="pj-row-card pj-card-hover" style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, cursor: 'pointer', transition: 'all 0.15s' }}
                  onClick={() => navigate(`/tickets/${t.id}`)}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#bfdbfe'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                >
                  <div style={{ minWidth: 0 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 4px' }}>{t.title}</h3>
                    <TicketBodyPreview ticket={t} />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', fontSize: 12, color: '#64748b' }}>
                      {t.company && <span>🏢 {t.company_display || t.company}</span>}
                      {t.department && <span>🏷️ {t.department_display || t.department}</span>}
                      <span style={{ color: '#94a3b8' }}>🕒 {timeAgo(t.assign_date || t.send_date)}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Badge variant={t.low_confidence ? 'amber' : t.assigned_by_ai ? 'purple' : 'blue'}>
                      {t.low_confidence ? '⚠️ ارجاع AI با اطمینان کم — بررسی شود' : t.assigned_by_ai ? '🤖 ارجاع توسط هوش‌مصنوعی' : '👤 ارجاع توسط دیسپچر'}
                    </Badge>
                    {t.can_reassign && (
                      <Btn size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); navigate(`/tickets/${t.id}/reassign`); }}>
                        ارجاع به شخص دیگر
                      </Btn>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
      }
    </div>
  );
}

// ─── Bulk AI Routing (for tickets left unrouted e.g. during an AI outage) ──
export function AiBulkRoutingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useUI();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [routing, setRouting] = useState(false);

  const load = () => {
    setLoading(true);
    ticketApi.getTicketListView({ dispatcher_seen: 0 })
      .then((res) => setTickets(Array.isArray(res.data) ? res.data : res.data?.tickets || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelected = tickets.length > 0 && selectedIds.size === tickets.length;
  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(tickets.map((t) => t.id || t.ticket_id)));
  };

  const routeSelected = async () => {
    if (selectedIds.size === 0) return;
    setRouting(true);
    try {
      const res = await ticketApi.aiRouteSelectedTickets(Array.from(selectedIds));
      const results = res.data?.results || [];
      const confidentCount = results.filter((r) => r.routed && !r.low_confidence).length;
      const lowConfidenceCount = results.filter((r) => r.routed && r.low_confidence).length;
      const notFoundCount = results.filter((r) => !r.routed && r.reason === 'دپارتمان مربوطه یافت نشد').length;
      const otherFailedCount = results.length - confidentCount - lowConfidenceCount - notFoundCount;
      if (confidentCount > 0) addToast(`${toPersian(confidentCount)} تیکت ارجاع شد`, 'success');
      if (lowConfidenceCount > 0) addToast(`${toPersian(lowConfidenceCount)} تیکت با اطمینان کم ارجاع شد — نیاز به بررسی دارد`, 'warning');
      // Not an error - AI unreachable/undecided is an expected, routine
      // outcome (not every ticket has a clear department, and the AI
      // service can be briefly down) - these tickets simply stay in the
      // dispatcher's own queue for manual routing, same as before this
      // button existed.
      if (notFoundCount > 0) addToast(`${toPersian(notFoundCount)} تیکت برای ارجاع دستی در صف دیسپچر باقی ماند`, 'info');
      if (otherFailedCount > 0) addToast(`${toPersian(otherFailedCount)} تیکت ارجاع نشد`, 'error');
      setSelectedIds(new Set());
      load();
    } catch (err) {
      addToast('خطا در ارجاع تیکت‌ها با هوش مصنوعی', 'error');
    }
    setRouting(false);
  };

  return (
    <div className="fade-in">
      <PageHeader title="ارجاع با هوش مصنوعی" subtitle={`${toPersian(tickets.length)} تیکت ارجاع‌نشده`} />

      <DashboardShortcuts role={user?.type || 'dispatcher'} activePath="/dispatcher/ai-routing" />

      {tickets.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#334155', cursor: 'pointer' }}>
            <input type="checkbox" checked={allSelected} onChange={toggleAll} />
            انتخاب همه
          </label>
          <Btn onClick={routeSelected} disabled={selectedIds.size === 0} loading={routing}>
            ارجاع منتخبین با هوش مصنوعی ({toPersian(selectedIds.size)})
          </Btn>
        </div>
      )}

      {loading
        ? <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{[1,2,3].map((k) => <SkeletonRow key={k} />)}</div>
        : tickets.length === 0
          ? <EmptyState icon="✅" title="صف خالی است" subtitle="تیکت ارجاع‌نشده‌ای وجود ندارد" />
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {tickets.map((t) => {
                const id = t.id || t.ticket_id;
                const checked = selectedIds.has(id);
                return (
                  <div key={id} className="pj-row-card pj-card-hover" style={{ background: '#fff', borderRadius: 12, border: `1px solid ${checked ? '#bfdbfe' : '#e2e8f0'}`, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', transition: 'all 0.15s' }}
                    onClick={() => toggleOne(id)}
                  >
                    <input type="checkbox" checked={checked} onChange={() => toggleOne(id)} onClick={(e) => e.stopPropagation()} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 4px' }}>{t.ticket_title || t.title}</h3>
                      <TicketBodyPreview ticket={t} />
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', fontSize: 12, color: '#64748b' }}>
                        {(t.sender_first_name || t.sender_last_name) && (
                          <span>👤 {[t.sender_first_name, t.sender_last_name].filter(Boolean).join(' ')}</span>
                        )}
                        {t.company ? <span>🏢 {t.company_display || t.company}</span> : <Badge variant="amber">مهمان</Badge>}
                        <span style={{ color: '#94a3b8' }}>🕒 {timeAgo(t.send_date)}</span>
                      </div>
                    </div>
                    <Btn size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); navigate(`/tickets/${id}/assign`); }}>
                      ارجاع دستی
                    </Btn>
                  </div>
                );
              })}
            </div>
          )
      }
    </div>
  );
}

// ─── Department Selection ────────────────────────────────────────────
export function TicketDeptSelectionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useUI();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const descRef = useRef(null);

  useEffect(() => {
    ticketApi.getTicketInfo(id).then((tRes) => {
      const d = tRes.data;
      const t = Array.isArray(d) ? d[0] : d;
      setTicket(t);
      return ticketApi.getDepartmentsListForCompany(t?.company_name);
    }).then((dRes) => {
      setDepartments(Array.isArray(dRes?.data?.departments) ? dRes.data.departments : []);
    }).catch(() => {})
      .finally(() => setLoading(false));

    // Restore whatever description/files were already staged for this
    // ticket (send_file -> dispatcher_draft_files) - previously nothing
    // ever fetched this back, so a page reload silently lost track of
    // already-uploaded attachments even though they were still staged
    // server-side.
    ticketApi.getDispatcherDraft(id)
      .then((res) => {
        const d = res.data || {};
        if (d.description) setDescription(d.description);
        const list = Array.isArray(d.files_list) ? d.files_list : [];
        setFiles(list.map((f) => ({ name: f.main_file_name, serverName: f.server_file_name, size: f.size })));
      })
      .catch(() => {});
  }, [id]);

  const handleFileAdd = async (f) => {
    if (!f) return;
    const validationError = validateFile(f, user?.type, files);
    if (validationError) { addToast(validationError, 'error'); return; }
    const dataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (ev) => resolve(ev.target.result);
      reader.readAsDataURL(f);
    });
    try {
      const res = await ticketApi.sendFile({ file_name: f.name, url: dataUrl, main_ticket_id: id });
      const serverName = res.data?.file_name || f.name;
      setFiles((prev) => [...prev, { name: f.name, serverName, size: f.size }]);
      addToast('فایل آپلود شد', 'success');
    } catch { addToast('خطا در آپلود فایل', 'error'); }
  };

  const removeFile = async (serverName, originalName) => {
    try { await ticketApi.deleteDispatcherFileFromDraft({ ticket_id: id, main_file_name: originalName }); } catch {}
    setFiles((prev) => prev.filter((f) => f.serverName !== serverName));
  };

  const assign = async () => {
    if (!selected) { addToast('یک دپارتمان انتخاب کنید', 'error'); return; }
    setSubmitting(true);
    try {
      await ticketApi.setTicketDepartment({ ticket_id: id, 'dept_name[]': [selected], description, priority: 0 });
      addToast('تیکت با موفقیت ارجاع شد', 'success');
      navigate('/dispatcher');
    } catch { addToast('خطا در ارجاع', 'error'); }
    setSubmitting(false);
  };

  return (
    <div className="fade-in">
      <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, marginBottom: 20 }}>← بازگشت</button>
      <PageHeader title="ارجاع تیکت" subtitle="دپارتمان مناسب را برای رسیدگی انتخاب کنید" />

      {loading ? <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{[1,2,3].map((k) => <SkeletonRow key={k} />)}</div> : (
        <div style={{ maxWidth: 600 }}>
          {ticket && (
            <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 20, marginBottom: 24 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>{ticket.ticket_title || ticket.title}</h3>
              <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, margin: 0 }}>{ticket.description || ticket.ticket_text || ticket.text}</p>
              {ticket.referral_roadmap?.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <TicketReferralRoadmap steps={ticket.referral_roadmap} viewerUsername={user?.username} compact />
                </div>
              )}
            </div>
          )}

          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#334155', marginBottom: 12 }}>انتخاب دپارتمان</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
            {departments.length === 0
              ? <p style={{ color: '#94a3b8', fontSize: 13 }}>دپارتمانی یافت نشد</p>
              : departments.map((name) => (
                <button key={name} onClick={() => setSelected(name)} style={{ padding: '14px 16px', borderRadius: 10, border: `2px solid ${selected === name ? '#3b82f6' : '#e2e8f0'}`, background: selected === name ? '#eff6ff' : '#fff', textAlign: 'right', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 12, transition: 'all 0.15s' }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: selected === name ? '#3b82f6' : '#e2e8f0', transition: 'background 0.15s' }} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: selected === name ? '#1d4ed8' : '#334155' }}>{name}</span>
                </button>
              ))
            }
          </div>

          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#334155', marginBottom: 12 }}>توضیحات دیسپچر</h4>
          <textarea ref={descRef} value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
            placeholder="توضیح یا یادداشت برای دپارتمان..."
            style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical', marginBottom: 8 }} />
          <div style={{ marginBottom: 20 }}>
            <EmojiBar inputRef={descRef} value={description} onInsert={setDescription} />
          </div>

          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#334155', marginBottom: 12 }}>پیوست‌ها</h4>
          <FileDropZone
            onFile={handleFileAdd}
            style={{ marginBottom: 10 }}
            accept={getAcceptAttribute(user?.type)}
            limitsHint={getLimitsHint(user?.type)}
          />
          {files.length > 0 && (
            <div style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {files.map((f) => (
                <div key={f.serverName} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f8faff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: 16 }}>📄</span>
                  <span style={{ flex: 1, fontSize: 12, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                  <button onClick={() => removeFile(f.serverName, f.name)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>✕</button>
                </div>
              ))}
            </div>
          )}

          <Btn onClick={assign} loading={submitting}>تأیید و ارجاع تیکت</Btn>
        </div>
      )}
    </div>
  );
}

export function TicketReassignPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useUI();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [selectedManager, setSelectedManager] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState('');
  const [files, setFiles] = useState([]);

  useEffect(() => {
    Promise.all([ticketApi.getTicketInfo(id), departmentApi.getDepartmentListView()])
      .then(([tRes, dRes]) => {
        const d = tRes.data;
        setTicket(Array.isArray(d) ? d[0] : d);
        setDepartments(Array.isArray(dRes.data) ? dRes.data.filter((row) => row.manager) : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const reassign = async () => {
    if (!selectedManager) { addToast('یک مدیر مقصد انتخاب کنید', 'error'); return; }
    setSubmitting(true);
    try {
      await ticketApi.employeeReferencingTicket({
        ticket_id: id,
        employee_id: selectedManager,
        note,
        files_json: files.map((f) => ({ file_name: f.file_name, data_url: f.data_url })),
      });
      addToast('تیکت با موفقیت ارجاع شد', 'success');
      navigate('/dispatcher/assigned');
    } catch (err) {
      addToast(err.response?.data || 'خطا در ارجاع تیکت', 'error');
    }
    setSubmitting(false);
  };

  return (
    <div className="fade-in">
      <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, marginBottom: 20 }}>← بازگشت</button>
      <PageHeader title="ارجاع به شخص دیگر" subtitle="تیکت هنوز پاسخ داده نشده — می‌توانید آن را به مدیر دپارتمان دیگری منتقل کنید" />

      {loading ? <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{[1, 2, 3].map((k) => <SkeletonRow key={k} />)}</div> : (
        <div style={{ maxWidth: 600 }}>
          {ticket && (
            <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 20, marginBottom: 24 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>{ticket.ticket_title || ticket.title}</h3>
              <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, margin: 0 }}>{ticket.description || ticket.ticket_text || ticket.text}</p>
              {ticket.referral_roadmap?.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <TicketReferralRoadmap steps={ticket.referral_roadmap} viewerUsername={user?.username} compact />
                </div>
              )}
            </div>
          )}

          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#334155', marginBottom: 12 }}>دپارتمان / مدیر مقصد</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
            {departments.length === 0
              ? <p style={{ color: '#94a3b8', fontSize: 13 }}>مدیر دپارتمانی یافت نشد</p>
              : departments.map((dept) => (
                <button key={dept.id} onClick={() => setSelectedManager(dept.manager)} style={{ padding: '14px 16px', borderRadius: 10, border: `2px solid ${selectedManager === dept.manager ? '#3b82f6' : '#e2e8f0'}`, background: selectedManager === dept.manager ? '#eff6ff' : '#fff', textAlign: 'right', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 12, transition: 'all 0.15s' }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: selectedManager === dept.manager ? '#3b82f6' : '#e2e8f0', transition: 'background 0.15s' }} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: selectedManager === dept.manager ? '#1d4ed8' : '#334155' }}>{dept.name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>مدیر: {dept.manager}</div>
                  </div>
                </button>
              ))
            }
          </div>

          <ReferralNoteComposer user={user} note={note} setNote={setNote} files={files} setFiles={setFiles} addToast={addToast} />
          <div style={{ marginTop: 20 }}>
            <Btn onClick={reassign} loading={submitting}>تأیید ارجاع</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

function SkeletonRow() {
  return <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0' }}><Skeleton h={18} w="50%" style={{ marginBottom: 8 }} /><Skeleton h={14} w="30%" /></div>;
}
